import React from 'react';
import styled from 'styled-components';
import { Box } from 'src/kit/Box';

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 24px;
  margin-top: 56px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingCard = styled.div<{ variant?: 'main' | 'secondary' }>`
  border-radius: 12px;
  padding: 40px;
  position: relative;
  overflow: hidden;

  ${(props) =>
    props.variant === 'main'
      ? `
    background: #0D1B2A; // var(--navy)
  `
      : `
    background: #F4F6F9; // var(--off-white)
    border: 1px solid #E8ECF0; // var(--light-gray)
    display: flex;
    flex-direction: column;
    gap: 0;
  `}
`;

const PricingBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #2dbd96; // var(--green)
`;

const PricingLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #2dbd96; // var(--green)
  margin-bottom: 12px;
`;

const PricingTitle = styled.div`
  font-family: 'Playfair Display', Georgia, serif; // var(--font-serif)
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff; // var(--white)
  line-height: 1.2;
  margin-bottom: 8px;
`;

const PricingSub = styled.div`
  font-size: 0.88rem;
  color: #8a99a8; // var(--mid-gray)
  line-height: 1.6;
  margin-bottom: 28px;
  font-style: italic;
`;

const PricingDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 20px 0;
`;

const PricingIncludesLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #8a99a8; // var(--mid-gray)
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 14px;
`;

const PricingList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const PricingListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.88rem;
  color: #e8ecf0; // var(--light-gray)
  margin-bottom: 10px;

  &::before {
    content: '✓';
    color: #2dbd96; // var(--green)
    font-weight: 700;
    flex-shrink: 0;
  }
`;

const PricingConversion = styled.div`
  padding: 24px;
  background: #f4f6f9; // var(--off-white)
  border-radius: 8px;
  border: 1px solid #e8ecf0; // var(--light-gray)
  margin-bottom: 16px;
  flex: 1;
`;

const PricingConversionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9a84c; // var(--gold)
  margin-bottom: 8px;
`;

const PricingConversionText = styled.div`
  font-size: 0.88rem;
  color: #8a99a8; // var(--mid-gray)
  line-height: 1.6;
`;

const PricingCtaBlock = styled.div`
  background: #2dbd96; // var(--green)
  border-radius: 8px;
  padding: 24px;
  text-align: center;
`;

const PricingCtaTitle = styled.div`
  font-family: 'Playfair Display', Georgia, serif; // var(--font-serif)
  font-size: 1.4rem;
  font-weight: 700;
  color: #0d1b2a; // var(--navy)
  margin-bottom: 6px;
`;

const PricingCtaSub = styled.div`
  font-size: 0.82rem;
  color: rgba(13, 27, 42, 0.7);
  margin-bottom: 16px;
  line-height: 1.5;
`;

const BtnDark = styled.a`
  background: #0d1b2a; // var(--navy)
  color: #ffffff; // var(--white)
  padding: 11px 24px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-block;
  transition: background 0.2s;

  &:hover {
    background: #1e3a5f; // var(--navy-lt)
  }
`;

const EmployerPricing = () => {
  return (
    <Box
      as="section"
      id="pricing"
      style={{ background: '#ffffff', borderTop: '1px solid #E8ECF0', padding: '100px 5%' }}
    >
      <Box style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Box style={{ textAlign: 'left', marginBottom: '70px' }}>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#2dbd96',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginBottom: '16px',
            }}
          >
            <Box as="span" style={{ display: 'block', width: '24px', height: '2px', background: '#2dbd96' }}></Box>
            Pricing
          </Box>
          <Box
            as="h2"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              fontWeight: 700,
              color: '#1A2332',
              marginBottom: '16px',
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
            }}
          >
            Your recruitment budget,
            <br />
            fixed from day one.
          </Box>
          <Box style={{ fontSize: '1.05rem', color: '#8a99a8', maxWidth: '560px', marginTop: '16px', lineHeight: 1.7 }}>
            No hidden fees. No per-CV charges. A licence scaled to your annual recruitment volume — so you know exactly
            what you're spending before the year starts.
          </Box>
        </Box>

        <PricingGrid>
          <PricingCard variant="main">
            <PricingBar />
            <PricingLabel>Annual Licence</PricingLabel>
            <PricingTitle>
              Calculated on your
              <br />
              recruitment volume
            </PricingTitle>
            <PricingSub>A fee tailored to the number of roles you publish each year — no surprises.</PricingSub>
            <PricingDivider />
            <PricingIncludesLabel>What's included</PricingIncludesLabel>
            <PricingList>
              <PricingListItem>Full platform access — Buy Side, Corporate, Infrastructure, Real Estate</PricingListItem>
              <PricingListItem>Distribution across 60+ European universities</PricingListItem>
              <PricingListItem>Integrated Corporate Finance tests + bespoke development available</PricingListItem>
              <PricingListItem>Dedicated Account Manager + candidate support</PricingListItem>
              <PricingListItem>Employer brand page — permanent visibility</PricingListItem>
            </PricingList>
          </PricingCard>

          <PricingCard variant="secondary">
            <PricingConversion>
              <PricingConversionLabel>Conversion Fee</PricingConversionLabel>
              <PricingConversionText>
                Only billed if the intern is subsequently hired into a permanent role. Zero risk on candidates who don't
                convert.
              </PricingConversionText>
            </PricingConversion>
            <PricingCtaBlock>
              <PricingCtaTitle>Request a quote</PricingCtaTitle>
              <PricingCtaSub>
                Custom pricing based on your annual recruitment volume. Response within 24h.
              </PricingCtaSub>
              <BtnDark href="mailto:elissa@sainoo.com">Get in touch →</BtnDark>
            </PricingCtaBlock>
          </PricingCard>
        </PricingGrid>
      </Box>
    </Box>
  );
};

export default EmployerPricing;
